import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ArrowLeft, Copy, Download, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import MatrixBackground from '@/MatrixBackground';
import AnimatedToolTitle from '@/components/AnimatedToolTitle';

const InstructionActionOutput = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { actionTitle, actionType, output, structuredData, triageData } = location.state || {};

  const handleCopy = () => {
    navigator.clipboard.writeText(output);
    toast.success('Content copied to clipboard!');
  };

  const handleDownload = () => {
    const blob = new Blob([output], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${actionType}_${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Document downloaded!');
  };

  if (!output) {
    navigate('/tools/instruction-action');
    return null;
  }

  return (
    <div className="tool-page">
      <MatrixBackground />
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => navigate('/tools/instruction-action')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Instruction & Action
        </Button>
        <div className="tool-title-section">
          <CheckCircle size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title={actionTitle} />
            <p className="tool-subtitle">Generated document ready for review</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {/* Context Information */}
        <Card className="result-card">
          <CardHeader>
            <CardTitle>Matter Context</CardTitle>
            <CardDescription>
              Information used to generate this document
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {structuredData && (
                <>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">Parties</h4>
                    <p className="text-sm text-muted-foreground">{structuredData.parties}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">Matter Type</h4>
                    <p className="text-sm text-muted-foreground">{structuredData.matterType}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-sm mb-1">Area of Law</h4>
                    <p className="text-sm text-muted-foreground">{triageData?.areaOfLaw}</p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Generated Output */}
        <Card className="result-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Generated Document</CardTitle>
                <CardDescription>
                  Review, copy, or download this document
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleCopy}
                  variant="outline"
                  size="sm"
                >
                  <Copy size={16} className="mr-2" />
                  Copy
                </Button>
                <Button
                  onClick={handleDownload}
                  variant="outline"
                  size="sm"
                >
                  <Download size={16} className="mr-2" />
                  Download
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="document-output">
              {output.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="output-paragraph">
                  {paragraph}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button
            onClick={() => navigate('/tools/instruction-action')}
            className="bg-[#D4AF37] text-black hover:bg-[#E5B200]"
          >
            Generate Another Document
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InstructionActionOutput;
